import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { FiStar } from 'react-icons/fi';
import { MatchData } from '@/interfaces/liveGamesType';
import { GroupedMatches } from '../../types/index';
import {
  MatchItem,
  SelectedMatchItem,
  LiveMatchItem,
  MatchListContainer,
  LeagueHeaderContainer
} from '../../styles';
import { isMatchLive } from '../../utils/index';
import styled, { keyframes } from 'styled-components';

const gleamingEffect = keyframes`
  0%, 100% {
    background-position: 0% 0;
  }
  50% {
    background-position: 200% 0;
  }
`;

const selectedPulse = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(42, 244, 151, 0.4);
  }
  50% {
    box-shadow: 0 0 25px rgba(42, 244, 151, 0.7);
  }
  100% {
    box-shadow: 0 0 10px rgba(42, 244, 151, 0.4);
  }
`;

// Componentes estilizados
const NeonMatchListContainer = styled(Box)`
  padding: 16px;
  height: 100%;
  overflow-y: auto;
  position: relative;
  background: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(42, 244, 151, 0.2);
  box-shadow: 0 0 15px rgba(42, 244, 151, 0.2);
  backdrop-filter: blur(5px);
  border-radius: 8px;
`;

const NeonLeagueHeader = styled(Box)`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: rgba(30, 30, 40, 0.7);
  border: 1px solid rgba(42, 244, 151, 0.15);
  box-shadow: 0 0 10px rgba(42, 244, 151, 0.1);
`;

const NeonMatchItem = styled(Box)`
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(25, 25, 35, 0.5);
  border: 1px solid rgba(42, 244, 151, 0.1);
  
  &:hover {
    background: rgba(30, 30, 40, 0.7);
    box-shadow: 0 0 15px rgba(42, 244, 151, 0.3);
    transform: translateY(-2px);
  }
`;

const NeonLiveMatchItem = styled(NeonMatchItem)`
  border-left: 4px solid rgba(42, 244, 151, 0.8);
`;

const NeonSelectedMatchItem = styled(NeonMatchItem)`
  background: rgba(30, 30, 40, 0.8);
  border: 1px solid rgba(42, 244, 151, 0.4);
  animation: ${selectedPulse} 3s infinite;
`;

const NeonStar = styled(FiStar)`
  color: rgba(42, 244, 151, 0.9);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.2);
    color: rgba(42, 244, 151, 1);
  }
`;

const LiveIndicator = styled(Typography)`
  color: rgba(42, 244, 151, 1);
  font-weight: bold;
  text-shadow: 0 0 5px rgba(42, 244, 151, 0.7);
  position: relative;
  background: rgba(42, 244, 151, 0.15);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 300%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.05) 15%,
      rgba(255, 255, 255, 0.2) 30%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.2) 70%,
      rgba(255, 255, 255, 0.05) 85%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${gleamingEffect} 4s ease-in-out infinite;
    pointer-events: none;
    z-index: 1;
  }
`;

const DotSpan = styled.span`
  margin-right: 2px;
`;

const LiveSpan = styled.span``;

const NeonText = styled(Typography)`
  color: #fff;
  text-shadow: 0 0 3px rgba(42, 244, 151, 0.3);
`;

const NeonScore = styled(Typography)`
  color: rgba(42, 244, 151, 0.9);
  font-weight: bold;
  text-shadow: 0 0 5px rgba(42, 244, 151, 0.5);
`;

const NeonTime = styled(Typography)`
  color: rgba(255, 255, 255, 0.7);
`;

const SkeletonContainer = styled(Box)`
  .MuiSkeleton-root {
    background: rgba(42, 244, 151, 0.1);
    
    &::after {
      background: linear-gradient(90deg, 
        transparent, 
        rgba(42, 244, 151, 0.2), 
        transparent
      );
    }
  }
`;

interface MatchesListProps {
  matches: MatchData[];
  loading: boolean;
  selectedMatch: MatchData | null;
  favorites: string[];
  groupedMatches: GroupedMatches;
  onSelectMatch: (match: MatchData) => void;
  toggleFavorite: (id: string) => void;
  isMobile?: boolean;
}

const MatchesList: React.FC<MatchesListProps> = ({
  matches,
  loading,
  selectedMatch,
  favorites,
  groupedMatches,
  onSelectMatch,
  toggleFavorite,
  isMobile
}) => {
  if (loading) {
    return (
      <NeonMatchListContainer>
        {[...Array(4)].map((_, leagueIndex) => (
          <SkeletonContainer key={`skeleton-league-${leagueIndex}`} mb={2} component="section">
            {/* Skeleton do cabeçalho da liga */}
            <NeonLeagueHeader>
              <Box display="flex" alignItems="center" width="100%">
                <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                <Skeleton variant="text" width={150} height={20} />
              </Box>
              <Skeleton variant="circular" width={24} height={24} />
            </NeonLeagueHeader>

            {/* Skeleton das partidas */}
            {[...Array(3)].map((_, matchIndex) => (
              <NeonMatchItem key={`skeleton-match-${leagueIndex}-${matchIndex}`}>
                {/* Horário e Status */}
                <Box width="40px" textAlign="center">
                  <Skeleton variant="text" width={30} height={14} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width={30} height={14} sx={{ mx: 'auto' }} />
                </Box>

                {isMobile ? (
                  <Box display="flex" flexDirection="column" flex={1} pl={1}>
                    {/* Time Visitante */}
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between" 
                      width="100%" 
                      mb={1}
                    >
                      <Box display="flex" alignItems="center">
                        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                        <Skeleton variant="text" width={80} height={16} />
                      </Box>
                      <Skeleton variant="text" width={15} height={16} />
                    </Box>

                    {/* Time da Casa */}
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between" 
                      width="100%"
                    >
                      <Box display="flex" alignItems="center">
                        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                        <Skeleton variant="text" width={80} height={16} />
                      </Box>
                      <Skeleton variant="text" width={15} height={16} />
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Time da Casa */}
                    <Box display="flex" alignItems="center" justifyContent="flex-end" flex={1} pr={1}>
                      <Skeleton variant="text" width={80} height={16} />
                      <Skeleton variant="circular" width={24} height={24} sx={{ ml: 1 }} />
                    </Box>

                    {/* Placar */}
                    <Box mx={1} minWidth={30} textAlign="center">
                      <Skeleton variant="text" width={30} height={16} sx={{ mx: 'auto' }} />
                    </Box>

                    {/* Time Visitante */}
                    <Box display="flex" alignItems="center" flex={1} pl={1}>
                      <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                      <Skeleton variant="text" width={80} height={16} />
                    </Box>
                  </>
                )}

                <Skeleton variant="circular" width={24} height={24} />
              </NeonMatchItem>
            ))}
          </SkeletonContainer>
        ))}
      </NeonMatchListContainer>
    );
  }

  if (matches.length === 0) {
    return (
      <NeonMatchListContainer sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <NeonText variant="body1">Nenhuma partida encontrada</NeonText>
      </NeonMatchListContainer>
    );
  }

  const isTeamHomeLooser = (match: MatchData) => {
    return Number(match.match_hometeam_score) < Number(match.match_awayteam_score);
  };

  const getLooserOpacity = (isLooser: boolean) => isLooser ? 0.7 : 1;

  return (
    <NeonMatchListContainer>
      {Object.entries(groupedMatches).map(([leagueId, data]) => (
        <Box key={leagueId} mb={2} component="section" aria-label={data.league}>
          {/* Header da Liga */}
          <NeonLeagueHeader>
            <Box display="flex" alignItems="center" width="100%">
              <Box
                component="img"
                src={data.leagueLogo}
                alt={data.country}
                width={24}
                height={24}
                mr={1}
                sx={{ 
                  filter: 'drop-shadow(0 0 2px rgba(42, 244, 151, 0.7))',
                  transition: 'all 0.3s ease',
                }}
                onError={(e: any) => { e.target.src = `https://ui-avatars.com/api/?name=${data.league}&background=2af497&color=121212` }}
              />
              <NeonText variant="subtitle2">{data.league}</NeonText>
            </Box>
            <IconButton
              size="small"
              onClick={() => toggleFavorite(leagueId)}
              aria-label={favorites.includes(leagueId) ? "Remover dos favoritos" : "Adicionar aos favoritos"}
              sx={{ 
                color: 'rgba(42, 244, 151, 0.9)',
                '&:hover': {
                  background: 'rgba(42, 244, 151, 0.1)',
                }
              }}
            >
              {favorites.includes(leagueId)
                ? <NeonStar fill="currentColor" />
                : <NeonStar />
              }
            </IconButton>
          </NeonLeagueHeader>

          {/* Lista de Partidas */}
          {data.matches.map(match => {
            const matchIsLive = isMatchLive(match);
            const isSelected = selectedMatch?.match_id === match.match_id;
            const homeTeamIsLooser = isTeamHomeLooser(match);
            const awayTeamIsLooser = !homeTeamIsLooser;

            // Escolher o componente base com base no estado da partida
            const MatchItemComponent = isSelected
              ? NeonSelectedMatchItem
              : matchIsLive
                ? NeonLiveMatchItem
                : NeonMatchItem;

            return (
              <MatchItemComponent
                key={match.match_id}
                onClick={() => onSelectMatch(match)}
                aria-label={`${match.match_hometeam_name} ${match.match_hometeam_score}-${match.match_awayteam_score} ${match.match_awayteam_name}`}
              >
                {/* Horário e Status */}
                <Box width="40px" textAlign="center">
                  <NeonTime variant="caption">
                    {match.match_time}
                  </NeonTime>
                  {matchIsLive ? (
                    <LiveIndicator variant="caption" display="block">
                      <DotSpan>•</DotSpan>
                      <LiveSpan>LIVE</LiveSpan>
                    </LiveIndicator>
                  ) : (
                    <NeonTime variant="caption" display="block">
                      {match.match_status}
                    </NeonTime>
                  )}
                </Box>

                {isMobile ? (
                  <Box display="flex" flexDirection="column" flex={1} pl={1}>
                    {/* Time Visitante */}
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between" 
                      width="100%" 
                      mb={1}
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          component="img"
                          src={match.team_away_badge}
                          alt={match.match_awayteam_name}
                          width={24}
                          height={24}
                          mr={1}
                          sx={{ 
                            opacity: getLooserOpacity(awayTeamIsLooser),
                            filter: !awayTeamIsLooser ? 'drop-shadow(0 0 3px rgba(42, 244, 151, 0.5))' : 'none',
                            transition: 'all 0.3s ease',
                          }}
                          onError={(e: any) => { e.target.src = `https://ui-avatars.com/api/?name=${match.match_awayteam_name}&background=2af497&color=121212` }}
                        />
                        <NeonText 
                          variant="body2" 
                          sx={{ opacity: getLooserOpacity(awayTeamIsLooser) }}
                        >
                          {match.match_awayteam_name}
                        </NeonText>
                      </Box>
                      <NeonScore 
                        variant="body2" 
                        sx={{ opacity: getLooserOpacity(awayTeamIsLooser) }}
                      >
                        {match.match_awayteam_score}
                      </NeonScore>
                    </Box>

                    {/* Time da Casa */}
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between" 
                      width="100%"
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          component="img"
                          src={match.team_home_badge}
                          alt={match.match_hometeam_name}
                          width={24}
                          height={24}
                          mr={1}
                          sx={{ 
                            opacity: getLooserOpacity(homeTeamIsLooser),
                            filter: !homeTeamIsLooser ? 'drop-shadow(0 0 3px rgba(42, 244, 151, 0.5))' : 'none',
                            transition: 'all 0.3s ease',
                          }}
                          onError={(e: any) => { e.target.src = `https://ui-avatars.com/api/?name=${match.match_hometeam_name}&background=2af497&color=121212` }}
                        />
                        <NeonText 
                          variant="body2"
                          sx={{ opacity: getLooserOpacity(homeTeamIsLooser) }}
                        >
                          {match.match_hometeam_name}
                        </NeonText>
                      </Box>
                      <NeonScore 
                        variant="body2"
                        sx={{ opacity: getLooserOpacity(homeTeamIsLooser) }}
                      >
                        {match.match_hometeam_score}
                      </NeonScore>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Time da Casa */}
                    <Box display="flex" alignItems="center" justifyContent="flex-end" flex={1} pr={1}>
                      <NeonText 
                        variant="body2"
                        sx={{ opacity: getLooserOpacity(homeTeamIsLooser) }}
                      >
                        {match.match_hometeam_name}
                      </NeonText>
                      <Box
                        component="img"
                        src={match.team_home_badge}
                        alt={match.match_hometeam_name}
                        width={24}
                        height={24}
                        ml={1}
                        sx={{ 
                          opacity: getLooserOpacity(homeTeamIsLooser),
                          filter: !homeTeamIsLooser ? 'drop-shadow(0 0 3px rgba(42, 244, 151, 0.5))' : 'none',
                          transition: 'all 0.3s ease',
                        }}
                        onError={(e: any) => { e.target.src = `https://ui-avatars.com/api/?name=${match.match_hometeam_name}&background=2af497&color=121212` }}
                      />
                    </Box>

                    {/* Placar */}
                    <Box mx={1} minWidth={30} textAlign="center">
                      <NeonScore variant="body2">
                        {match.match_hometeam_score}-{match.match_awayteam_score}
                      </NeonScore>
                    </Box>

                    {/* Time Visitante */}
                    <Box display="flex" alignItems="center" flex={1} pl={1}>
                      <Box
                        component="img"
                        src={match.team_away_badge}
                        alt={match.match_awayteam_name}
                        width={24}
                        height={24}
                        mr={1}
                        sx={{ 
                          opacity: getLooserOpacity(awayTeamIsLooser),
                          filter: !awayTeamIsLooser ? 'drop-shadow(0 0 3px rgba(42, 244, 151, 0.5))' : 'none',
                          transition: 'all 0.3s ease',
                        }}
                        onError={(e: any) => { e.target.src = `https://ui-avatars.com/api/?name=${match.match_awayteam_name}&background=2af497&color=121212` }}
                      />
                      <NeonText 
                        variant="body2"
                        sx={{ opacity: getLooserOpacity(awayTeamIsLooser) }}
                      >
                        {match.match_awayteam_name}
                      </NeonText>
                    </Box>
                  </>
                )}

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(match.match_id);
                  }}
                  aria-label={favorites.includes(match.match_id) ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                  sx={{ 
                    color: 'rgba(42, 244, 151, 0.9)',
                    '&:hover': {
                      background: 'rgba(42, 244, 151, 0.1)',
                    }
                  }}
                >
                  {favorites.includes(match.match_id)
                    ? <NeonStar fill="currentColor" />
                    : <NeonStar />
                  }
                </IconButton>
              </MatchItemComponent>
            );
          })}
        </Box>
      ))}
    </NeonMatchListContainer>
  );
};

export default MatchesList;